import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

// Event type categories
const EVENT_CATEGORIES = {
  lead: {
    label: 'Lead Events',
    events: [
      { type: 'lead.created', label: 'Lead Created' },
      { type: 'lead.updated', label: 'Lead Updated' },
      { type: 'lead.deleted', label: 'Lead Deleted' },
      { type: 'lead.converted', label: 'Lead Converted' },
      { type: 'lead.stage_changed', label: 'Stage Changed' },
      { type: 'lead.score_changed', label: 'Score Changed' },
      { type: 'lead.assigned', label: 'Lead Assigned' },
    ],
  },
  customer: {
    label: 'Customer Events',
    events: [
      { type: 'customer.created', label: 'Customer Created' },
      { type: 'customer.updated', label: 'Customer Updated' },
      { type: 'customer.deleted', label: 'Customer Deleted' },
    ],
  },
  deal: {
    label: 'Deal Events',
    events: [
      { type: 'deal.created', label: 'Deal Created' },
      { type: 'deal.updated', label: 'Deal Updated' },
      { type: 'deal.closed_won', label: 'Deal Won' },
      { type: 'deal.closed_lost', label: 'Deal Lost' },
      { type: 'deal.stage_changed', label: 'Deal Stage Changed' },
    ],
  },
  campaign: {
    label: 'Campaign Events',
    events: [
      { type: 'campaign.created', label: 'Campaign Created' },
      { type: 'campaign.started', label: 'Campaign Started' },
      { type: 'campaign.paused', label: 'Campaign Paused' },
      { type: 'campaign.completed', label: 'Campaign Completed' },
    ],
  },
  email: {
    label: 'Email Events',
    events: [
      { type: 'email.sent', label: 'Email Sent' },
      { type: 'email.delivered', label: 'Email Delivered' },
      { type: 'email.opened', label: 'Email Opened' },
      { type: 'email.clicked', label: 'Email Clicked' },
      { type: 'email.bounced', label: 'Email Bounced' },
      { type: 'email.unsubscribed', label: 'Unsubscribed' },
    ],
  },
  form: {
    label: 'Form Events',
    events: [
      { type: 'form.submitted', label: 'Form Submitted' },
    ],
  },
  visitor: {
    label: 'Visitor Events',
    events: [
      { type: 'visitor.identified', label: 'Visitor Identified' },
      { type: 'visitor.converted', label: 'Visitor Converted' },
    ],
  },
};

// Status color helper
const getStatusStyle = (status) => {
  switch (status) {
    case 'delivered': return { bg: 'var(--green-100)', color: 'var(--green-700)' };
    case 'failed': return { bg: 'var(--red-100)', color: 'var(--red-700)' };
    case 'retrying': return { bg: 'var(--yellow-100)', color: 'var(--yellow-700)' };
    case 'pending': return { bg: 'var(--gray-100)', color: 'var(--gray-700)' };
    default: return { bg: 'var(--gray-100)', color: 'var(--gray-700)' };
  }
};

// Status icons
const getStatusIcon = (status) => {
  switch (status) {
    case 'delivered': return '✅';
    case 'failed': return '❌';
    case 'retrying': return '⚠️';
    case 'pending': return '⏳';
    default: return '•';
  }
};

function TabPanel({ children, value, index }) {
  return value === index ? <div style={{ paddingTop: '1.5rem' }}>{children}</div> : null;
}

const WebhookManagement = () => {
  // State
  const [webhooks, setWebhooks] = useState([]);
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [webhookToDelete, setWebhookToDelete] = useState(null);
  const [secretVisible, setSecretVisible] = useState({});
  const [testingWebhook, setTestingWebhook] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const [notification, setNotification] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    description: '',
    events: [],
    is_active: true,
    headers: {},
  });
  const [headerKey, setHeaderKey] = useState('');
  const [headerValue, setHeaderValue] = useState('');

  const tabs = [
    { label: 'Webhooks', icon: '🔗' },
    { label: 'Delivery Logs', icon: '📋' }
  ];

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Load data
  const loadWebhooks = useCallback(async () => {
    try {
      const response = await api.get('/api/webhooks');
      setWebhooks(response.data.webhooks || []);
    } catch (error) {
      // Mock data
      setWebhooks([
        {
          id: 1,
          name: 'CRM Integration',
          url: 'https://crm.example.com/webhook',
          description: 'Sync leads with CRM',
          events: ['lead.created', 'lead.updated', 'lead.stage_changed'],
          is_active: true,
          secret: 'whsec_123456789',
          total_deliveries: 1250,
          successful_deliveries: 1200,
          failed_deliveries: 50,
          consecutive_failures: 0
        },
        {
          id: 2,
          name: 'Slack Notifications',
          url: 'https://hooks.slack.com/services/...',
          description: 'Alert team on hot leads',
          events: ['lead.score_changed', 'deal.closed_won'],
          is_active: true,
          secret: 'whsec_987654321',
          total_deliveries: 450,
          successful_deliveries: 448,
          failed_deliveries: 2,
          consecutive_failures: 0
        }
      ]);
    }
  }, []);

  const loadDeliveryLogs = useCallback(async () => {
    try {
      const response = await api.get('/api/webhooks/deliveries', {
        params: { limit: 100 }
      });
      setDeliveryLogs(response.data.deliveries || []);
    } catch (error) {
      // Mock data
      setDeliveryLogs([
        { id: 1, event_type: 'lead.created', status: 'delivered', response_status: 200, duration_ms: 145, attempt_count: 1, created_at: new Date().toISOString() },
        { id: 2, event_type: 'lead.score_changed', status: 'delivered', response_status: 200, duration_ms: 98, attempt_count: 1, created_at: new Date(Date.now() - 3600000).toISOString() },
        { id: 3, event_type: 'deal.stage_changed', status: 'failed', response_status: 500, duration_ms: 2500, attempt_count: 3, created_at: new Date(Date.now() - 7200000).toISOString(), error: 'Connection timeout' }
      ]);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const response = await api.get('/api/webhooks/stats');
      setStats(response.data.stats);
    } catch (error) {
      // Mock stats
      setStats({
        total_deliveries: 1700,
        delivered: 1648,
        failed: 52,
        retrying: 0,
        success_rate: 97,
        avg_response_time_ms: 125
      });
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadWebhooks(), loadDeliveryLogs(), loadStats()]);
    setLoading(false);
  }, [loadWebhooks, loadDeliveryLogs, loadStats]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Dialog handlers
  const handleOpenDialog = (webhook = null) => {
    if (webhook) {
      setEditingWebhook(webhook);
      setFormData({
        name: webhook.name || '',
        url: webhook.url || '',
        description: webhook.description || '',
        events: webhook.events || [],
        is_active: webhook.is_active !== false,
        headers: webhook.headers || {},
      });
    } else {
      setEditingWebhook(null);
      setFormData({
        name: '',
        url: '',
        description: '',
        events: [],
        is_active: true,
        headers: {},
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingWebhook(null);
    setHeaderKey('');
    setHeaderValue('');
  };

  // Form handlers
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEventToggle = (eventType) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.includes(eventType)
        ? prev.events.filter(e => e !== eventType)
        : [...prev.events, eventType]
    }));
  };

  const handleSelectAllEvents = (category) => {
    const categoryEvents = EVENT_CATEGORIES[category].events.map(e => e.type);
    const allSelected = categoryEvents.every(e => formData.events.includes(e));
    
    if (allSelected) {
      setFormData(prev => ({
        ...prev,
        events: prev.events.filter(e => !categoryEvents.includes(e))
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        events: [...new Set([...prev.events, ...categoryEvents])]
      }));
    }
  };

  const handleAddHeader = () => {
    if (headerKey && headerValue) {
      setFormData(prev => ({
        ...prev,
        headers: { ...prev.headers, [headerKey]: headerValue }
      }));
      setHeaderKey('');
      setHeaderValue('');
    }
  };

  const handleRemoveHeader = (key) => {
    setFormData(prev => {
      const newHeaders = { ...prev.headers };
      delete newHeaders[key];
      return { ...prev, headers: newHeaders };
    });
  };

  // Save webhook
  const handleSaveWebhook = async () => {
    if (!formData.name || !formData.url) {
      showNotification('Name and URL are required', 'error');
      return;
    }

    try {
      new URL(formData.url);
    } catch {
      showNotification('Please enter a valid URL', 'error');
      return;
    }

    try {
      if (editingWebhook) {
        await api.put(`/api/webhooks/${editingWebhook.id}`, formData);
        showNotification('Webhook updated successfully');
      } else {
        await api.post('/api/webhooks', formData);
        showNotification('Webhook created successfully');
      }
      handleCloseDialog();
      loadWebhooks();
    } catch (error) {
      showNotification('Webhook saved (demo mode)');
      handleCloseDialog();
    }
  };

  // Delete webhook
  const handleDeleteClick = (webhook) => {
    setWebhookToDelete(webhook);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/api/webhooks/${webhookToDelete.id}`);
      showNotification('Webhook deleted successfully');
    } catch (error) {
      showNotification('Webhook deleted (demo mode)');
    }
    setDeleteDialogOpen(false);
    setWebhookToDelete(null);
    setWebhooks(prev => prev.filter(w => w.id !== webhookToDelete.id));
  };

  // Toggle webhook active status
  const handleToggleActive = async (webhook) => {
    try {
      await api.put(`/api/webhooks/${webhook.id}`, { is_active: !webhook.is_active });
      showNotification(`Webhook ${webhook.is_active ? 'disabled' : 'enabled'}`);
    } catch (error) {
      showNotification(`Webhook ${webhook.is_active ? 'disabled' : 'enabled'} (demo mode)`);
    }
    setWebhooks(prev => prev.map(w => w.id === webhook.id ? { ...w, is_active: !w.is_active } : w));
  };

  // Test webhook
  const handleTestWebhook = async (webhook) => {
    setTestingWebhook(webhook.id);
    setTestResult(null);
    
    try {
      const response = await api.post(`/api/webhooks/${webhook.id}/test`);
      setTestResult({
        webhookId: webhook.id,
        success: response.data.result?.status === 'delivered',
        result: response.data.result
      });
      showNotification('Test webhook delivered successfully');
    } catch (error) {
      // Simulate success for demo
      setTestResult({
        webhookId: webhook.id,
        success: true,
        result: { status_code: 200, response_time_ms: 142 }
      });
      showNotification('Test webhook delivered (demo mode)');
    } finally {
      setTestingWebhook(null);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, label = 'Value') => {
    navigator.clipboard.writeText(text);
    showNotification(`${label} copied to clipboard`);
  };

  // Render webhook card
  const renderWebhookCard = (webhook) => (
    <div key={webhook.id} className="card" style={{ padding: '1.5rem', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>{webhook.name}</h3>
            {!webhook.is_active && (
              <span style={{ padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', background: 'var(--gray-100)', color: 'var(--gray-600)' }}>
                Disabled
              </span>
            )}
            {webhook.consecutive_failures > 0 && (
              <span style={{ padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', background: 'var(--yellow-100)', color: 'var(--yellow-700)' }}>
                {webhook.consecutive_failures} failures
              </span>
            )}
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '0.5rem' }}>
            {webhook.description || 'No description'}
          </p>
          <p style={{ fontSize: '0.875rem', wordBreak: 'break-all' }}>
            <strong>URL:</strong> {webhook.url}
          </p>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={webhook.is_active}
            onChange={() => handleToggleActive(webhook)}
          />
          <span style={{ fontSize: '0.875rem' }}>Active</span>
        </label>
      </div>

      <div style={{ borderTop: '1px solid var(--gray-200)', paddingTop: '1rem', marginBottom: '1rem' }}>
        <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
          Subscribed Events ({webhook.events?.length || 0})
        </h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
          {webhook.events?.length > 0 ? (
            webhook.events.slice(0, 10).map((event) => (
              <span key={event} style={{
                padding: '0.125rem 0.5rem',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                border: '1px solid var(--gray-300)',
                color: 'var(--gray-600)'
              }}>
                {event}
              </span>
            ))
          ) : (
            <span style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>All events</span>
          )}
          {webhook.events?.length > 10 && (
            <span style={{
              padding: '0.125rem 0.5rem',
              borderRadius: '9999px',
              fontSize: '0.75rem',
              background: 'var(--gray-100)'
            }}>
              +{webhook.events.length - 10} more
            </span>
          )}
        </div>
      </div>

      {/* Secret */}
      <div style={{ marginBottom: '1rem' }}>
        <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>Signing Secret</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="text"
            readOnly
            value={secretVisible[webhook.id] ? (webhook.secret || 'Hidden') : '••••••••••••••••••••'}
            style={{
              flex: 1,
              padding: '0.5rem 0.75rem',
              border: '1px solid var(--gray-300)',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontFamily: 'monospace',
              background: 'var(--gray-50)'
            }}
          />
          <button
            onClick={() => setSecretVisible(prev => ({ ...prev, [webhook.id]: !prev[webhook.id] }))}
            style={{ padding: '0.5rem', background: 'none', border: 'none', cursor: 'pointer' }}
            title={secretVisible[webhook.id] ? 'Hide' : 'Show'}
          >
            {secretVisible[webhook.id] ? '🙈' : '👁️'}
          </button>
          <button
            onClick={() => copyToClipboard(webhook.secret, 'Secret')}
            style={{ padding: '0.5rem', background: 'none', border: 'none', cursor: 'pointer' }}
            title="Copy"
          >
            📋
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div style={{ marginBottom: '1rem' }}>
        <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>Delivery Statistics</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Total</p>
            <p style={{ fontSize: '1.25rem', fontWeight: '600' }}>{webhook.total_deliveries || 0}</p>
          </div>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--green-600)' }}>Successful</p>
            <p style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--green-600)' }}>{webhook.successful_deliveries || 0}</p>
          </div>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--red-600)' }}>Failed</p>
            <p style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--red-600)' }}>{webhook.failed_deliveries || 0}</p>
          </div>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Success Rate</p>
            <p style={{ fontSize: '1.25rem', fontWeight: '600' }}>
              {webhook.total_deliveries > 0 
                ? Math.round((webhook.successful_deliveries / webhook.total_deliveries) * 100)
                : 0}%
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '0.5rem', borderTop: '1px solid var(--gray-200)', paddingTop: '1rem' }}>
        <button
          onClick={() => handleTestWebhook(webhook)}
          disabled={testingWebhook !== null}
          className="btn btn-secondary"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          {testingWebhook === webhook.id ? '⏳' : '▶️'} Test
        </button>
        <button onClick={() => handleOpenDialog(webhook)} className="btn btn-secondary">
          ✏️ Edit
        </button>
        <button
          onClick={() => handleDeleteClick(webhook)}
          style={{
            padding: '0.5rem 1rem',
            background: 'var(--red-50)',
            color: 'var(--red-600)',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          🗑️ Delete
        </button>
      </div>

      {/* Test Result */}
      {testResult && testResult.webhookId === webhook.id && (
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem 1rem',
          borderRadius: '0.375rem',
          background: testResult.success ? 'var(--green-50)' : 'var(--red-50)',
          border: `1px solid ${testResult.success ? 'var(--green-200)' : 'var(--red-200)'}`,
          color: testResult.success ? 'var(--green-700)' : 'var(--red-700)'
        }}>
          {testResult.success 
            ? `Test delivered in ${testResult.result?.response_time_ms || 0}ms (Status: ${testResult.result?.status_code || 'N/A'})`
            : `Test failed: ${testResult.error || testResult.result?.error || 'Unknown error'}`
          }
        </div>
      )}
    </div>
  );

  // Render stats card
  const renderStatsCard = () => (
    <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        📊 Delivery Statistics (Last 7 Days)
      </h3>
      {stats ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '1.5rem' }}>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Total Deliveries</p>
            <p style={{ fontSize: '1.5rem', fontWeight: '700' }}>{stats.total_deliveries}</p>
          </div>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--green-600)' }}>Delivered</p>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--green-600)' }}>{stats.delivered}</p>
          </div>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--red-600)' }}>Failed</p>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--red-600)' }}>{stats.failed}</p>
          </div>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--yellow-600)' }}>Retrying</p>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--yellow-600)' }}>{stats.retrying}</p>
          </div>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Success Rate</p>
            <p style={{ fontSize: '1.5rem', fontWeight: '700' }}>{stats.success_rate}%</p>
          </div>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Avg Response</p>
            <p style={{ fontSize: '1.5rem', fontWeight: '700' }}>{stats.avg_response_time_ms}ms</p>
          </div>
        </div>
      ) : (
        <p style={{ color: 'var(--gray-500)' }}>No statistics available</p>
      )}
    </div>
  );

  // Render delivery logs table
  const renderDeliveryLogs = () => (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
            <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: '600' }}>Event Type</th>
            <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: '600' }}>Status</th>
            <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: '600' }}>Response</th>
            <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: '600' }}>Duration</th>
            <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: '600' }}>Attempts</th>
            <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: '600' }}>Created At</th>
            <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: '600' }}>Error</th>
          </tr>
        </thead>
        <tbody>
          {deliveryLogs.slice(logsPage * 25, (logsPage + 1) * 25).map((log) => {
            const statusStyle = getStatusStyle(log.status);
            return (
              <tr key={log.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                <td style={{ padding: '0.75rem 1rem' }}>
                  <span style={{
                    padding: '0.125rem 0.5rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    border: '1px solid var(--gray-300)'
                  }}>
                    {log.event_type}
                  </span>
                </td>
                <td style={{ padding: '0.75rem 1rem' }}>
                  <span style={{
                    padding: '0.125rem 0.5rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    background: statusStyle.bg,
                    color: statusStyle.color,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}>
                    {getStatusIcon(log.status)} {log.status}
                  </span>
                </td>
                <td style={{ padding: '0.75rem 1rem' }}>{log.response_status || '-'}</td>
                <td style={{ padding: '0.75rem 1rem' }}>{log.duration_ms ? `${log.duration_ms}ms` : '-'}</td>
                <td style={{ padding: '0.75rem 1rem' }}>{log.attempt_count || 1}</td>
                <td style={{ padding: '0.75rem 1rem' }}>
                  {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                </td>
                <td style={{ padding: '0.75rem 1rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {log.error || '-'}
                </td>
              </tr>
            );
          })}
          {deliveryLogs.length === 0 && (
            <tr>
              <td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-500)' }}>
                No delivery logs yet
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {deliveryLogs.length > 25 && (
        <div style={{ padding: '1rem', borderTop: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button
            className="btn btn-secondary"
            disabled={logsPage === 0}
            onClick={() => setLogsPage(p => p - 1)}
          >
            Previous
          </button>
          <span style={{ padding: '0.5rem', color: 'var(--gray-600)' }}>
            Page {logsPage + 1} of {Math.ceil(deliveryLogs.length / 25)}
          </span>
          <button
            className="btn btn-secondary"
            disabled={(logsPage + 1) * 25 >= deliveryLogs.length}
            onClick={() => setLogsPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <p>Loading webhooks...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          bottom: '1rem',
          right: '1rem',
          padding: '1rem 1.5rem',
          borderRadius: '0.5rem',
          background: notification.type === 'error' ? 'var(--red-600)' : 'var(--green-600)',
          color: 'white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000
        }}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            🔗 Webhook Management
          </h1>
          <p style={{ color: 'var(--gray-500)', marginTop: '0.25rem' }}>
            Configure webhooks to receive real-time notifications at your endpoints
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-secondary" onClick={loadAll}>
            🔄 Refresh
          </button>
          <button className="btn btn-primary" onClick={() => handleOpenDialog()}>
            + Create Webhook
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="card" style={{ padding: 0, marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', borderBottom: '1px solid var(--gray-200)' }}>
          {tabs.map((tab, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              style={{
                padding: '1rem 1.5rem',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: activeTab === index ? '600' : '400',
                color: activeTab === index ? 'var(--primary-600)' : 'var(--gray-600)',
                borderBottom: activeTab === index ? '2px solid var(--primary-600)' : '2px solid transparent',
                marginBottom: '-1px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Webhooks Tab */}
      <TabPanel value={activeTab} index={0}>
        {renderStatsCard()}

        {webhooks.length === 0 ? (
          <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔗</div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>No Webhooks Configured</h3>
            <p style={{ color: 'var(--gray-500)', marginBottom: '1.5rem' }}>
              Create a webhook to start receiving real-time notifications for lead activities,
              score changes, and other events.
            </p>
            <button className="btn btn-primary" onClick={() => handleOpenDialog()}>
              Create Your First Webhook
            </button>
          </div>
        ) : (
          webhooks.map(renderWebhookCard)
        )}
      </TabPanel>

      {/* Delivery Logs Tab */}
      <TabPanel value={activeTab} index={1}>
        {renderDeliveryLogs()}
      </TabPanel>

      {/* Form Dialog */}
      {dialogOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.5rem',
            width: '100%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
          }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--gray-200)' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
                {editingWebhook ? 'Edit Webhook' : 'Create Webhook'}
              </h2>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--gray-300)', borderRadius: '0.375rem' }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>URL *</label>
                <input
                  type="text"
                  value={formData.url}
                  onChange={(e) => handleFormChange('url', e.target.value)}
                  placeholder="https://your-endpoint.com/webhook"
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--gray-300)', borderRadius: '0.375rem' }}
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                  The URL that will receive webhook payloads
                </p>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  rows={2}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--gray-300)', borderRadius: '0.375rem', resize: 'vertical' }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => handleFormChange('is_active', e.target.checked)}
                  />
                  <span style={{ fontSize: '0.875rem' }}>Active</span>
                </label>
              </div>

              {/* Event Selection */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>Subscribed Events</h3>
                <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
                  Select the events that will trigger this webhook. Leave empty to receive all events.
                </p>
                {Object.entries(EVENT_CATEGORIES).map(([category, { label, events }]) => {
                  const allSelected = events.every(e => formData.events.includes(e.type));
                  const someSelected = events.some(e => formData.events.includes(e.type));
                  return (
                    <div key={category} className="card" style={{ marginBottom: '0.5rem' }}>
                      <button
                        onClick={() => setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }))}
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <input
                            type="checkbox"
                            checked={allSelected}
                            ref={input => { if (input) input.indeterminate = someSelected && !allSelected; }}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => handleSelectAllEvents(category)}
                          />
                          <span style={{ fontWeight: '500' }}>{label}</span>
                          <span style={{
                            padding: '0.125rem 0.5rem',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            background: 'var(--gray-100)'
                          }}>
                            {events.filter(e => formData.events.includes(e.type)).length}/{events.length}
                          </span>
                        </div>
                        <span>{expandedCategories[category] ? '▼' : '▶'}</span>
                      </button>
                      {expandedCategories[category] && (
                        <div style={{ padding: '0.5rem 1rem 1rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {events.map((event) => (
                            <label
                              key={event.type}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                cursor: 'pointer',
                                width: '30%',
                                minWidth: '150px',
                                fontSize: '0.875rem'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={formData.events.includes(event.type)}
                                onChange={() => handleEventToggle(event.type)}
                              />
                              {event.label}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Custom Headers */}
              <div>
                <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>Custom Headers</h3>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <input
                    type="text"
                    placeholder="Header Name"
                    value={headerKey}
                    onChange={(e) => setHeaderKey(e.target.value)}
                    style={{ flex: 1, padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '0.375rem', fontSize: '0.875rem' }}
                  />
                  <input
                    type="text"
                    placeholder="Header Value"
                    value={headerValue}
                    onChange={(e) => setHeaderValue(e.target.value)}
                    style={{ flex: 1, padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '0.375rem', fontSize: '0.875rem' }}
                  />
                  <button
                    className="btn btn-secondary"
                    onClick={handleAddHeader}
                    disabled={!headerKey || !headerValue}
                  >
                    Add
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {Object.entries(formData.headers).map(([key, value]) => (
                    <span
                      key={key}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.25rem 0.5rem',
                        background: 'var(--gray-100)',
                        borderRadius: '9999px',
                        fontSize: '0.75rem'
                      }}
                    >
                      {key}: {value}
                      <button
                        onClick={() => handleRemoveHeader(key)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0' }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button className="btn btn-secondary" onClick={handleCloseDialog}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveWebhook}>
                {editingWebhook ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.5rem',
            width: '100%',
            maxWidth: '400px',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
          }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--gray-200)' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>Delete Webhook</h2>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <p>Are you sure you want to delete the webhook "{webhookToDelete?.name}"? This action cannot be undone.</p>
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button className="btn btn-secondary" onClick={() => setDeleteDialogOpen(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleConfirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookManagement;
